import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import AdminDataTable from "../../../components/common/AdminDataTable";
import { can } from "../../../utils/can";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CMSFaqManager from "../../../components/common/CMSFaqManager";

const SELECT2_CSS =
  "https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/css/select2.min.css";
const SELECT2_CDN =
  "https://cdn.jsdelivr.net/npm/select2@4.1.0-rc.0/dist/js/select2.min.js";

function ensureSelect2Loaded() {
  return new Promise((resolve, reject) => {
    const $ = window.jQuery || window.$;
    if (!$) {
      reject(new Error("jQuery not found"));
      return;
    }
    if ($.fn && $.fn.select2) {
      resolve($);
      return;
    }
    if (!document.querySelector(`link[href="${SELECT2_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = SELECT2_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${SELECT2_CDN}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve($));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = SELECT2_CDN;
    script.onload = () => resolve($);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

const PAGE_TYPE = "caste_page";
const slugify = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function CMSCastePage() {
  const API_URL = (process.env.REACT_APP_BASE_URL_API || "").replace(/\/$/, "");
  const token = localStorage.getItem("adminToken");
  const PAGE_SIZE = 10;

  const [castes, setCastes] = useState([]);
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const emptyForm = {
    id: null,
    caste_ids: [],
    casteName: "",
    slug: "",
    aboutTitle1: "",
    aboutTitle2: "",
    content: "",
    whyChooseTitle: "",
    whyChooseUs: "",
    sectionTitle: "",
    sectionContent: "",
    faqTitle: "",
    faqs: [],
    metaTitle: "",
    metaKeywords: "",
    customScript: "",
    metaDescription: "",
    status: "Active",
  };
  const [form, setForm] = useState(emptyForm);
  const casteSelectRef = useRef(null);
  const isSelect2Updating = useRef(false);

  const fetchCastes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms-page/home-castes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) setCastes(result.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [API_URL, token]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/cms-page-list?pageType=${PAGE_TYPE}&page=${page}&pageSize=${pageSize}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await res.json();
      if (result.success) {
        setList(result.data || []);
        setTotalPages(result.totalPages || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, page, pageSize]);

  useEffect(() => {
    fetchCastes();
  }, [fetchCastes]);
  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Init Select2 on caste multi-select (searchable)
  useEffect(() => {
    const el = casteSelectRef.current;
    if (!el || !castes.length) return;

    let destroyed = false;
    let $el = null;

    ensureSelect2Loaded()
      .then(($) => {
        if (destroyed || !casteSelectRef.current) return;
        $el = $(casteSelectRef.current);

        if ($el.hasClass("select2-hidden-accessible")) {
          try {
            $el.select2("destroy");
          } catch (e) {
            /* ignore */
          }
        }

        $el.select2({
          placeholder: "Search & select castes (min 1)",
          allowClear: true,
          width: "100%",
          closeOnSelect: false,
        });

        // Sync current selection after init
        isSelect2Updating.current = true;
        $el.val(form.caste_ids || []).trigger("change");
        isSelect2Updating.current = false;

        $el.off("change.casteSelect").on("change.casteSelect", function () {
          if (isSelect2Updating.current) return;
          const selected = $el.val() || [];
          setForm((prev) => ({
            ...prev,
            caste_ids: Array.isArray(selected) ? selected : [selected],
          }));
        });
      })
      .catch((err) => console.error("Select2 load failed:", err));

    return () => {
      destroyed = true;
      if ($el && $el.hasClass("select2-hidden-accessible")) {
        $el.off("change.casteSelect");
        try {
          $el.select2("destroy");
        } catch (e) {
          /* ignore */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [castes]);

  // Keep Select2 in sync when editing / resetting form
  useEffect(() => {
    const el = casteSelectRef.current;
    if (!el) return;
    const $ = window.jQuery || window.$;
    if (!$ || !$.fn?.select2) return;
    const $el = $(el);
    if (!$el.hasClass("select2-hidden-accessible")) return;
    isSelect2Updating.current = true;
    $el.val(form.caste_ids || []).trigger("change");
    isSelect2Updating.current = false;
  }, [form.caste_ids, form.id]);

  const handleCasteNameChange = (value) => {
    const rawSlug = slugify(value);
    const slug = rawSlug ? (rawSlug.endsWith("-matrimony") ? rawSlug : `${rawSlug}-matrimony`) : "";
    setForm((prev) => ({
      ...prev,
      casteName: value,
      slug,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (can("cms_caste_page", form.id ? "edit" : "create") === false) {
      alert("You don't have permission.");
      return;
    }
    if (!form.casteName?.trim()) {
      alert("Please enter caste name.");
      return;
    }
    if (!form.caste_ids || form.caste_ids.length < 1) {
      alert("Please select at least 1 caste.");
      return;
    }

    setLoading(true);
    setMessage("");
    const url = form.id
      ? `${API_URL}/api/admin/cms-page-list/update/${form.id}`
      : `${API_URL}/api/admin/cms-page-create`;

    try {
      const payload = {
        pageType: PAGE_TYPE,
        ...form,
        id: undefined,
        caste_ids: form.caste_ids,
        caste_id: form.caste_ids[0] || null,
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.success) {
        setMessage("Saved!");
        setForm(emptyForm);
        fetchList();
      } else {
        alert(result.message || "Error");
      }
    } catch (err) {
      alert("Server error.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    if (can("cms_caste_page", "edit") === false) {
      alert("No permission.");
      return;
    }

    const selectedIds =
      Array.isArray(row.caste_ids) && row.caste_ids.length > 0
        ? row.caste_ids
            .map((c) => (typeof c === "object" ? c?._id : c))
            .filter(Boolean)
        : row.caste_id?._id
          ? [row.caste_id._id]
          : [];

    setForm({
      id: row._id,
      caste_ids: selectedIds,
      casteName: row.casteName || "",
      slug: row.slug || slugify(row.casteName || ""),
      aboutTitle1: row.aboutTitle1 || "",
      aboutTitle2: row.aboutTitle2 || "",
      content: row.content || "",
      whyChooseTitle: row.whyChooseTitle || "",
      whyChooseUs: row.whyChooseUs || "",
      sectionTitle: row.sectionTitle || "",
      sectionContent: row.sectionContent || "",
      faqTitle: row.faqTitle || "",
      faqs: Array.isArray(row.faqs) ? row.faqs : [],
      metaTitle: row.metaTitle || "",
      metaKeywords: row.metaKeywords || "",
      customScript: row.customScript || "",
      metaDescription: row.metaDescription || "",
      status: row.status || "Active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (can("cms_caste_page", "delete") === false) {
      alert("No permission.");
      return;
    }
    if (!window.confirm("Delete?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/cms-page-list/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) fetchList();
      else alert(result.message);
    } catch (err) {
      alert("Server error.");
    }
  };

  const columns = [
    {
      label: "S.No",
      width: 50,
      render: (r, i) => (page - 1) * pageSize + i + 1,
    },
    {
      label: "Caste Name",
      render: (r) => r.casteName || "-",
    },
    {
      label: "Selected Castes",
      render: (r) =>
        Array.isArray(r.caste_ids) && r.caste_ids.length
          ? r.caste_ids.map((c) => c.name || c).join(", ")
          : r.caste_id?.name || "-",
    },
    { label: "Slug", render: (r) => r.slug || "-" },
    { label: "About Title 1", render: (r) => r.aboutTitle1 || "-" },
    {
      label: "Status",
      width: 90,
      render: (r) => (
        <span className={r.status === "Active" ? "text-success" : "text-danger"}>
          {r.status}
        </span>
      ),
    },
    {
      label: "Action",
      width: 120,
      render: (r) => (
        <ul className="actionlist p-0">
          <li>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleEdit(r);
              }}
            >
              <i className="fa fa-edit" />
            </a>
          </li>
          <li>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDelete(r._id);
              }}
            >
              <i className="fa fa-trash" />
            </a>
          </li>
        </ul>
      ),
    },
  ];

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="CMS - Caste Page" />
        <div className="right_col" role="main">
          <div className="x_panel">
            <div className="x_title">
              <h2>{form.id ? "Edit" : "Add"} Caste Page</h2>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 col-sm-4 mb-3">
                    <label>Caste Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.casteName}
                      onChange={(e) => handleCasteNameChange(e.target.value)}
                    />
                  </div>

                  <div className="col-12 col-sm-4 mb-3">
                    <label>Caste Dropdown - Select min 1 (All Active)</label>
                    <select
                      ref={casteSelectRef}
                      className="form-select caste-select2"
                      multiple
                      defaultValue={form.caste_ids}
                      style={{ width: "100%" }}
                    >
                      {castes.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name} ({c.religion_id?.name || ""})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Search and select multiple castes. Selected:{" "}
                      {form.caste_ids.length}
                    </small>
                  </div>

                  <div className="col-12 col-sm-4 mb-3">
                    <label>Slug (from Caste Name)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.slug}
                      onChange={(e) =>
                        setForm({ ...form, slug: slugify(e.target.value) })
                      }
                    />
                  </div>

                  <div className="col-12 col-sm-4 mb-3">
                    <label>About Title 1</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.aboutTitle1}
                      onChange={(e) =>
                        setForm({ ...form, aboutTitle1: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-12 col-sm-4 mb-3">
                    <label>About Title 2</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.aboutTitle2}
                      onChange={(e) =>
                        setForm({ ...form, aboutTitle2: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12 mb-3">
                    <label>Content</label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={form.content}
                      onChange={(_, ed) =>
                        setForm((p) => ({ ...p, content: ed.getData() }))
                      }
                    />
                  </div>
                  <div className="col-12 col-sm-6 mb-3">
                    <label>Why Choose Title / Heading (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.whyChooseTitle}
                      onChange={(e) =>
                        setForm({ ...form, whyChooseTitle: e.target.value })
                      }
                      placeholder="e.g. Best Marriage Bureau in Community"
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label>Why Choose Us Content</label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={form.whyChooseUs}
                      onChange={(_, ed) =>
                        setForm((p) => ({ ...p, whyChooseUs: ed.getData() }))
                      }
                    />
                  </div>

                  <div className="col-12 col-sm-6 mb-3">
                    <label>Section 3 Title / Bottom Heading (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.sectionTitle}
                      onChange={(e) =>
                        setForm({ ...form, sectionTitle: e.target.value })
                      }
                      placeholder="e.g. Best Matrimonial Services for Community"
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label>Section 3 Content / Bottom Content (Optional)</label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={form.sectionContent}
                      onChange={(_, ed) =>
                        setForm((p) => ({ ...p, sectionContent: ed.getData() }))
                      }
                    />
                  </div>

                  <CMSFaqManager
                    faqTitle={form.faqTitle}
                    onChangeFaqTitle={(faqTitle) => setForm((p) => ({ ...p, faqTitle }))}
                    faqs={form.faqs}
                    onChangeFaqs={(faqs) => setForm((p) => ({ ...p, faqs }))}
                  />

                  <div className="col-md-12 mb-3">
                    <label>Meta Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.metaTitle}
                      onChange={(e) =>
                        setForm({ ...form, metaTitle: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label>Meta Keywords</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.metaKeywords}
                      onChange={(e) =>
                        setForm({ ...form, metaKeywords: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label>Custom Scripts (GTM, Schema, Analytics)</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder="<script type=&quot;application/ld+json&quot;>&#10;...&#10;</script>"
                      value={form.customScript}
                      onChange={(e) =>
                        setForm({ ...form, customScript: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label>Meta Description</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.metaDescription}
                      onChange={(e) =>
                        setForm({ ...form, metaDescription: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label>Status</label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value })
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="col-12 text-right">
                    <button
                      type="submit"
                      className="btn sitebtn me-2"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Submit"}
                    </button>
                    {form.id && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setForm(emptyForm)}
                      >
                        Cancel
                      </button>
                    )}
                    {message && (
                      <span className="text-success ms-2">{message}</span>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="x_panel">
            <div className="x_title">
              <h2>Caste Page List</h2>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <AdminDataTable
                columns={columns}
                data={list}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={listLoading}
                onPageChange={setPage}
                onPageSizeChange={(s) => {
                  setPageSize(s);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
